import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, XCircle, LogIn, Loader2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useCustomer } from "../context/CustomerContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import Invoice from "../components/Invoice";
import RescheduleDialog from "../components/RescheduleDialog";

const canChangeIn24h = (b) => new Date(`${b.date}T${b.time}:00+03:00`) - Date.now() >= 24 * 3600 * 1000;

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-rose-100 text-rose-700",
  rejected: "bg-rose-100 text-rose-700",
  completed: "bg-sky-100 text-sky-700",
  awaiting_payment: "bg-orange-100 text-orange-700",
};

export default function MyBookings() {
  const { t } = useApp();
  const { user: customer, startLogin } = useCustomer();
  const [params] = useSearchParams();
  const [reference, setReference] = useState(params.get("ref") || "");
  const [phone, setPhone] = useState("");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [myList, setMyList] = useState(null);
  const [showResched, setShowResched] = useState(false);

  useEffect(() => {
    if (customer) {
      setMyList(null);
      api.get("/my/bookings").then((r) => setMyList(r.data)).catch(() => setMyList([]));
    } else {
      setMyList(null);
    }
  }, [customer]);

  const lookup = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setBooking(null);
    try {
      const { data } = await api.post("/bookings/lookup", { reference, phone });
      setBooking(data);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm(t.my.confirmCancel)) return;
    try {
      await api.post("/bookings/cancel", { reference, phone });
      toast.success(t.my.cancelled);
      lookup();
    } catch (err) {
      toast.error(apiErr(err));
    }
  };

  return (
    <div className="pt-28 sm:pt-32">
      <div className="px-4 sm:px-8 lg:px-16 max-w-3xl mx-auto pb-8">
        <div className="text-center mb-10">
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-foreground">{t.my.title}</h1>
          <p className="text-muted-foreground mt-3 text-lg">{t.my.subtitle}</p>
        </div>

        {/* Logged-in customer bookings */}
        {customer ? (
          <div className="mb-8" data-testid="customer-bookings">
            <h2 className="font-bold mb-3">
              {t.nav.myBookings} — <span className="text-primary">{customer.name || customer.email}</span>
            </h2>
            {myList === null ? (
              <div className="grid place-items-center py-10"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
            ) : myList.length === 0 ? (
              <p className="text-muted-foreground text-center py-8 rounded-2xl bg-card cb-card-shadow">{t.common.empty}</p>
            ) : (
              <div className="space-y-3">
                {myList.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => { setReference(b.reference); setPhone(b.customer.phone); setBooking(b); }}
                    className="w-full text-start rounded-2xl bg-card cb-card-shadow p-4 flex items-center justify-between hover:bg-muted/40 transition-colors"
                    data-testid={`my-booking-${b.id}`}
                  >
                    <div>
                      <div className="font-semibold" dir="ltr">{b.reference}</div>
                      <div className="text-sm text-muted-foreground">{b.date} · {b.time} · {b.total} {t.common.sar}</div>
                    </div>
                    <Badge className={`${STATUS_COLORS[b.status]} border-0 rounded-full px-3 py-1`}>{t.status[b.status] || b.status}</Badge>
                  </button>
                ))}
              </div>
            )}
            <div className="text-center mt-6 text-sm text-muted-foreground">— {t.my.subtitle} —</div>
          </div>
        ) : (
          <div className="mb-8 rounded-2xl bg-secondary/40 p-5 flex items-center justify-between no-print">
            <span className="text-sm font-medium">سجّلي الدخول لعرض كل حجوزاتكِ تلقائياً</span>
            <Button onClick={startLogin} variant="outline" className="rounded-full border-2 font-semibold" data-testid="mybookings-login-btn">
              <LogIn className="h-4 w-4 me-2" /> {t.common.login}
            </Button>
          </div>
        )}

        <form onSubmit={lookup} className="rounded-3xl bg-card cb-card-shadow p-6 sm:p-8 grid sm:grid-cols-2 gap-4 no-print">
          <div>
            <Label className="mb-1.5 block">{t.my.reference}</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="CB-XXXXXXXX" required data-testid="lookup-reference-input" className="rounded-xl h-12" />
          </div>
          <div>
            <Label className="mb-1.5 block">{t.my.phone}</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="05XXXXXXXX" required data-testid="lookup-phone-input" className="rounded-xl h-12" dir="ltr" />
          </div>
          <Button type="submit" disabled={loading} className="sm:col-span-2 rounded-full h-12 font-semibold" data-testid="lookup-submit-btn">
            <Search className="h-4 w-4 me-2" /> {loading ? t.common.loading : t.my.lookup}
          </Button>
        </form>

        {booking && (
          <div className="mt-8 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Badge className={`${STATUS_COLORS[booking.status]} border-0 rounded-full px-4 py-1.5 text-sm`} data-testid="booking-status-badge">
                {t.status[booking.status] || booking.status}
              </Badge>
              <div className="flex flex-wrap gap-2 no-print">
              {["pending", "confirmed"].includes(booking.status) && (
                <Button
                  onClick={() => setShowResched(true)}
                  disabled={(booking.reschedule_count || 0) >= 2 || !canChangeIn24h(booking)}
                  variant="outline"
                  className="rounded-full border-2 font-semibold"
                  data-testid="reschedule-booking-btn"
                >
                  <CalendarClock className="h-4 w-4 me-2" /> {t.my.reschedule}
                </Button>
              )}
              {["pending", "confirmed", "awaiting_payment"].includes(booking.status) && (
                <Button onClick={cancel} variant="outline" className="rounded-full border-2 text-destructive border-destructive/40 no-print" data-testid="cancel-booking-btn">
                  <XCircle className="h-4 w-4 me-2" /> {t.my.cancelBooking}
                </Button>
              )}
              </div>
            </div>
            {["pending", "confirmed"].includes(booking.status) && (
              <p className="text-sm text-muted-foreground no-print" data-testid="reschedule-info">
                {(booking.reschedule_count || 0) >= 2 ? t.my.rescheduleLimit
                  : !canChangeIn24h(booking) ? t.my.rescheduleTooLate
                  : t.my.rescheduleLeft.replace("{n}", 2 - (booking.reschedule_count || 0))}
              </p>
            )}
            <RescheduleDialog
              open={showResched}
              onOpenChange={setShowResched}
              booking={booking}
              phone={phone}
              onDone={(b) => { setBooking(b); setMyList((l) => l && l.map((x) => (x.id === b.id ? b : x))); }}
            />
            <Invoice booking={booking} />
          </div>
        )}
      </div>
    </div>
  );
}
